import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import {
  FormControl,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeading,
  ModalProps,
  Textarea,
} from '@uikit';

import { useAddUserReviewMutation } from '@query';

import { DeserializedAppData } from '@api';

import Star from '@uikit/icons/Star';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { getErrorMessage } from '@shared/helpers/error';

type AddUserReviewModalProps = Omit<ModalProps, 'children'> & {
  app: DeserializedAppData;
};

const AddUserReviewModal = ({ app, ...props }: AddUserReviewModalProps) => {
  const { user, isAuth } = useAuth();
  const { mutateAsync: addUserReview } = useAddUserReviewMutation();
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<{
    title: string;
    text: string;
    rating: number;
  }>();
  const handleFormSubmit = handleSubmit(async (data) => {
    if (!isAuth) {
      toast.error('Not authorized');

      return;
    }

    props.onOpenChange?.(false);

    try {
      await addUserReview({
        id: app.id,
        appId: app.attributes.appLink,
        user,
        ...data,
        rating: Number(data.rating),
      });

      reset();
    } catch (error) {
      const message = getErrorMessage(error, 'Something went wrong');

      toast.error(`Failed to add review. ${message}`);
    }
  });

  return (
    <Modal {...props}>
      <ModalContent className="flex flex-col gap-8 p-10 w-full max-w-xl">
        <ModalCloseButton className="absolute right-4 top-4" />
        <header>
          <ModalHeading className="m-0">User review</ModalHeading>
        </header>
        <form id="add-user-review-form" className="flex flex-col gap-7" onSubmit={handleFormSubmit}>
          <div>
            <Controller
              control={control}
              name="rating"
              render={({ field: { value: strValue, ...field }, fieldState }) => {
                const value = Number(strValue);

                return (
                  <FormControl
                    label="Rating"
                    visuallyRequired
                    isError={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    <ul className="flex gap-2">
                      {Array.from({ length: 5 }, (_, i) => {
                        const radioValue = i + 1;
                        const isChecked = radioValue === value;
                        const isActive = value >= radioValue;

                        return (
                          <li key={i}>
                            <label>
                              <input
                                type="radio"
                                value={radioValue}
                                className="sr-only peer"
                                checked={isChecked}
                                {...field}
                              />
                              <Star
                                className={classNames(
                                  'icon rounded-full transition-colors cursor-pointer',
                                  'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary',
                                  isActive
                                    ? 'text-secondary'
                                    : 'text-neutral-100 hover:text-secondary-200',
                                )}
                              />
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </FormControl>
                );
              }}
            />
          </div>
          <div>
            <FormControl
              label="Title"
              placeholder="Title"
              visuallyRequired
              isError={!!errors.title}
              helperText={errors.title?.message}
              Field={Input}
              {...register('title', {
                required: 'Field is required',
                validate: {
                  minLength: (value) => {
                    if (value && value.trim().length < 5) {
                      return 'Minimum length is 5 characters';
                    }
                  },
                  maxLength: (value) => {
                    if (value && value.trim().length > 40) {
                      return 'Maximum length is 40 characters';
                    }
                  },
                },
              })}
            />
          </div>
          <div>
            <FormControl
              label="Text"
              placeholder="Text"
              visuallyRequired
              isError={!!errors.text}
              helperText={errors.text?.message}
              showLetterCount
              maxLength={4000}
              Field={Textarea}
              {...register('text', {
                required: 'Field is required',
                validate: {
                  minLength: (value) => {
                    if (value && value.trim().length < 100) {
                      return 'Minimum length is 100 characters';
                    }
                  },
                  maxLength: (value) => {
                    if (value && value.trim().length > 4000) {
                      return 'Maximum length is 4000 characters';
                    }
                  },
                },
              })}
            />
          </div>
        </form>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            className="button button-outlined button-lg text-neutral-600"
            onClick={() => props.onOpenChange?.(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-user-review-form"
            className="button button-solid button-lg button-color-primary min-w-[7.5rem]"
          >
            Submit
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AddUserReviewModal;
